import { formatISODate } from "@clipboard-health/date-time";
import { isDefined, isNullOrUndefined } from "@clipboard-health/util-ts";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers-pro";
import { showSuccessToast } from "@src/appV2/lib/Notifications";
import { LoadingButton, Text, type UseModalState } from "@src/lib/ui-react";
import { addDays, getDay, startOfDay } from "date-fns";
import { useEffect, useRef } from "react";
import { Controller, useForm } from "react-hook-form";

import { useUpdateWorkplaceInvalidContractWarning } from "./api/useUpdateWorkplaceInvalidContractWarning";
import { invalidContractWarningFormSchema } from "./types";

interface FormInputs {
  enabled: boolean;
  contractFixDeadline?: Date;
  contractSigningUrl?: string;
}

interface EditInvalidContractWarningDialogProps {
  modalState: UseModalState;
  workplaceId: string;
  invalidContractWarningSettings?: {
    enabled: boolean;
    contractFixDeadline?: Date;
    contractSigningUrl?: string;
  };
  onClose: () => void;
}

const MINIMUM_WEEKS_IN_FUTURE = 4;
const MONDAY = 1;
const TUESDAY = 2;

export function EditInvalidContractWarningDialog(props: EditInvalidContractWarningDialogProps) {
  const { modalState, workplaceId, invalidContractWarningSettings, onClose } = props;
  const { enabled, contractFixDeadline, contractSigningUrl } = invalidContractWarningSettings ?? {};
  const { isLoading: isUpdateLoading, mutateAsync: updateWorkplace } =
    useUpdateWorkplaceInvalidContractWarning();

  function isValidContractFixDeadline(date: Date): boolean {
    const dayOfWeek = getDay(date);
    const isMondayOrTuesday = dayOfWeek === MONDAY || dayOfWeek === TUESDAY;
    const minimumDate = startOfDay(addDays(new Date(), MINIMUM_WEEKS_IN_FUTURE * 7));
    return isMondayOrTuesday && date >= minimumDate;
  }

  const { control, handleSubmit, watch, reset, formState } = useForm({
    defaultValues: { enabled: enabled ?? false, contractFixDeadline, contractSigningUrl },
    resolver: zodResolver(invalidContractWarningFormSchema),
  });

  const { isValid, errors } = formState;

  const settingsEnabled = watch("enabled");

  const previousModalIsOpen = useRef(false);
  useEffect(() => {
    if (modalState.modalIsOpen && !previousModalIsOpen.current) {
      reset(
        { enabled: !(enabled ?? false), contractFixDeadline, contractSigningUrl },
        { keepDefaultValues: true }
      );
    }
    previousModalIsOpen.current = modalState.modalIsOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalState.modalIsOpen]);

  const handleResetAndClose = () => {
    reset({ enabled: enabled ?? false, contractFixDeadline, contractSigningUrl });
    onClose();
  };

  const updateSettings = async (params: FormInputs): Promise<boolean> => {
    const {
      enabled: isEnabled,
      contractFixDeadline: deadline,
      contractSigningUrl: signingUrl,
    } = params;
    return updateWorkplace({
      workplaceId,
      invalidContractWarningSettings: {
        enabled: isEnabled,
        ...(isDefined(deadline) && {
          contractFixDeadline: formatISODate(deadline),
        }),
        ...(isDefined(signingUrl) && signingUrl.length > 0 && { contractSigningUrl: signingUrl }),
      },
    }).then(
      (response) => {
        if (isDefined(response)) {
          showSuccessToast("Updated invalid contract warning settings successfully");
        }
        return true;
      },
      () => false
    );
  };

  return (
    <Dialog open={modalState.modalIsOpen} onClose={handleResetAndClose}>
      <DialogTitle>Invalid Contract Warning Settings</DialogTitle>
      <DialogContent>
        <form
          onSubmit={handleSubmit(async (data) => {
            const {
              enabled: isEnabled,
              contractFixDeadline: deadline,
              contractSigningUrl: signingUrl,
            } = data;
            const updatePayload: FormInputs = isEnabled
              ? {
                  enabled: true,
                  ...(isDefined(deadline) && { contractFixDeadline: deadline }),
                  ...(isDefined(signingUrl) &&
                    signingUrl.length > 0 && { contractSigningUrl: signingUrl }),
                }
              : { enabled: false };
            const success = await updateSettings(updatePayload);
            if (success) {
              reset(updatePayload);
              onClose();
            }
          })}
        >
          <Stack direction="column" spacing={2} alignItems="flex-start">
            <Controller
              name="enabled"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Switch
                      color="primary"
                      checked={Boolean(field.value)}
                      onChange={(event) => {
                        field.onChange(event.target.checked);
                      }}
                    />
                  }
                  label={
                    <Text variant="body1" sx={{ marginRight: 4 }}>
                      Invalid Contract Warning Enabled
                    </Text>
                  }
                  labelPlacement="start"
                />
              )}
            />
            <Controller
              name="contractFixDeadline"
              control={control}
              render={({ field }) => (
                <DatePicker
                  {...field}
                  disablePast
                  label="Contract fix deadline"
                  disabled={!settingsEnabled}
                  value={field.value}
                  inputRef={field.ref}
                  shouldDisableDate={(date) => !isValidContractFixDeadline(date)}
                  slotProps={{
                    textField: {
                      error: Boolean(
                        settingsEnabled &&
                        (errors?.contractFixDeadline ?? isNullOrUndefined(field.value))
                      ),
                      helperText:
                        settingsEnabled &&
                        (errors?.contractFixDeadline ?? isNullOrUndefined(field.value)) &&
                        "Please select a Monday or Tuesday at least 4 weeks in the future.",
                    },
                  }}
                  onChange={(date) => {
                    field.onChange(date);
                  }}
                />
              )}
            />
            <Controller
              name="contractSigningUrl"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  value={field.value ?? ""}
                  label="Contract signing URL (optional)"
                  type="url"
                  disabled={!settingsEnabled}
                  error={Boolean(errors?.contractSigningUrl)}
                  helperText={errors?.contractSigningUrl?.message}
                />
              )}
            />
            <Stack direction="row" spacing={1} alignItems="center" marginTop={2}>
              <Button onClick={handleResetAndClose}>Cancel</Button>
              <LoadingButton
                type="submit"
                variant="contained"
                isLoading={isUpdateLoading}
                disabled={!isValid}
              >
                Submit
              </LoadingButton>
            </Stack>
          </Stack>
        </form>
      </DialogContent>
    </Dialog>
  );
}
